import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
} from '@tanstack/react-router'
import { HomeView } from './App'
import { CapabilityStatementPlaceholder } from './components/CapabilityStatementPlaceholder'

/**
 * Sprint 21 §1.10 — TanStack Router config.
 *
 * Replaces the prior hand-rolled `window.location.pathname` + `popstate`
 * switch in App.tsx. Route shape per the user-confirmed N3 clarification:
 *
 *   /                              HomeView (landing + sizer + compare + methodology)
 *   /compare                       HomeView, autoscrolled to #compare on mount
 *   /methodology                   HomeView, autoscrolled to #methodology on mount
 *   /capability-statement-coming-soon  CapabilityStatementPlaceholder
 *
 * `/pdf` is a public-api endpoint (POST /api/public/pdf/generate),
 * not a frontend route — it does NOT appear here.
 *
 * Routing posture: code-based, no file-based plugin. The app is small
 * and route count is stable through Sprint 21; revisit if Sprint 22+
 * pushes route count past ~6.
 */

const rootRoute = createRootRoute({
  component: () => <Outlet />,
})

const homeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: HomeView,
})

const compareRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/compare',
  component: HomeView,
})

const methodologyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/methodology',
  component: HomeView,
})

function CapabilityPlaceholderRoute() {
  return (
    <CapabilityStatementPlaceholder
      onBack={() => {
        // Imperative navigation for the "← Back" affordance. Using
        // history.pushState + dispatching popstate lets TanStack Router
        // pick up the URL change without a full reload.
        history.pushState(null, '', '/')
        window.dispatchEvent(new PopStateEvent('popstate'))
      }}
    />
  )
}

const capabilityComingSoonRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/capability-statement-coming-soon',
  component: CapabilityPlaceholderRoute,
})

const routeTree = rootRoute.addChildren([
  homeRoute,
  compareRoute,
  methodologyRoute,
  capabilityComingSoonRoute,
])

export const router = createRouter({ routeTree })

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}
